import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Oportunidade, RetratoAnalise, StatusOportunidade } from '../tipos';

/**
 * Oportunidade: a análise que virou decisão de alguém.
 *
 * Uma análise favorável não vale nada solta — sem dono e sem prazo ela vira uma
 * aba aberta que ninguém fecha. O que transforma um número em ação é ter alguém
 * responsável e uma decisão registrada.
 *
 * ⚠️ O planejamento previa gravar a ANÁLISE por Cloud Function, para que ela
 * fosse reproduzível e não pudesse ser forjada. Isso ainda não existe: o motor
 * de cálculo mora em `packages/calculo`, e a pasta `functions` é empacotada
 * sozinha no deploy. Enquanto isso, a oportunidade guarda o RETRATO dos números
 * que estavam na tela, e quem a criou fica registrado. É menos garantia que o
 * plano pede, e está escrito aqui para não passar por pronto.
 */

export const ROTULO_STATUS: Record<StatusOportunidade, string> = {
  ABERTA: 'Aberta',
  EM_NEGOCIACAO: 'Em negociação',
  COMPRADA: 'Comprada',
  DESCARTADA: 'Descartada',
  ARQUIVADA: 'Arquivada',
};

export const COR_STATUS: Record<StatusOportunidade, string> = {
  ABERTA: 'text-est bg-est/12',
  EM_NEGOCIACAO: 'text-c-res bg-c-res/12',
  COMPRADA: 'text-c-via bg-c-via/12',
  DESCARTADA: 'text-tinta-3 bg-tinta-3/12',
  ARQUIVADA: 'text-tinta-3 bg-tinta-3/12',
};

export function useOportunidades() {
  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'oportunidades'), orderBy('criadoEm', 'desc')),
        (s) => {
          setOportunidades(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Oportunidade));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { oportunidades, carregando, erro };
}

export interface NovaOportunidade {
  produtoId: string;
  fornecedorId: string | null;
  classificacao: Oportunidade['classificacao'];
  lucroTotalCents: number;
  roiBps: number;
  retrato: RetratoAnalise;
}

/**
 * Guarda a oportunidade com o retrato dos números.
 *
 * Nasce ABERTA e sem responsável: atribuir dono é uma decisão de quem gerencia,
 * e assumir que quem analisou vai executar cria dono nominal que não sabe que é.
 */
export async function criarOportunidade(
  dados: NovaOportunidade,
  usuarioId: string,
): Promise<string> {
  const ref = await addDoc(collection(db, 'oportunidades'), {
    ...dados,
    analiseId: null,
    status: 'ABERTA' satisfies StatusOportunidade,
    responsavelId: null,
    reservadaAte: null,
    favoritaDe: [],
    decisao: null,
    observacoes: null,
    criadoEm: serverTimestamp(),
    criadoPor: usuarioId,
    atualizadoEm: null,
    atualizadoPor: null,
  });
  return ref.id;
}

export async function atualizarOportunidade(
  id: string,
  dados: Partial<Pick<Oportunidade, 'status' | 'responsavelId' | 'decisao' | 'observacoes'>>,
  usuarioId: string,
): Promise<void> {
  await updateDoc(doc(db, 'oportunidades', id), {
    ...dados,
    atualizadoEm: serverTimestamp(),
    atualizadoPor: usuarioId,
  });
}

export const removerOportunidade = (id: string) => deleteDoc(doc(db, 'oportunidades', id));

/**
 * O que está de fato em jogo: capital comprometido e lucro esperado das
 * oportunidades vivas.
 *
 * Conta só ABERTA e EM_NEGOCIACAO. Somar as compradas diria quanto já foi
 * gasto, não quanto ainda está em decisão — e é a segunda pergunta que o painel
 * responde.
 */
export function resumoDasVivas(oportunidades: readonly Oportunidade[]) {
  const vivas = oportunidades.filter(
    (o) => o.status === 'ABERTA' || o.status === 'EM_NEGOCIACAO',
  );
  return {
    quantas: vivas.length,
    capitalCents: vivas.reduce((s, o) => s + (o.retrato?.capitalNecessarioCents ?? 0), 0),
    lucroCents: vivas.reduce((s, o) => s + (o.lucroTotalCents ?? 0), 0),
    emNegociacao: vivas.filter((o) => o.status === 'EM_NEGOCIACAO').length,
    semResponsavel: vivas.filter((o) => !o.responsavelId).length,
  };
}
